<template>
  <v-container class="pa-1" fluid>
    <v-form ref="form">
      <v-row dense>
        <v-col v-if="$store.state.Auth.userProperties.system_settings.delegation_order_type !== 1" cols="12">
          <delegation-order-select
            :delegation-id="delegation.id"
            :delegation-order-id.sync="delegation.delegation_order_id"
            :delegation-order-number.sync="delegation.delegation_order_number"
            :error-messages="delegationErrors.delegation_order_id"
          />
        </v-col>
        <v-col md="6" sm="6" cols="12">
          <v-autocomplete
            :error-messages="delegationErrors.user_id"
            :items="users"
            :label="$t('delegation.form.fields.delegated.name')"
            item-text="fullName"
            item-value="id"
            v-model="contextUser"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col md="6" sm="6" cols="12" v-if="companies.length > 1">
          <v-autocomplete
            :error-messages="delegationErrors.company_id"
            :items="companies"
            :label="$t('delegation.form.fields.company.name')"
            item-text="name"
            item-value="id"
            v-model="delegation.company_id"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col md="6" sm="6" cols="12">
          <v-tooltip :disabled="$store.state.Auth.userProperties.system_settings.delegation_number_setting_type !== 2" top>
            <template #activator="{on}">
              <v-text-field
                :disabled="$store.state.Auth.userProperties.system_settings.delegation_number_setting_type === 3"
                :error-messages="delegationErrors.custom_number"
                :label="$t('delegation.form.fields.delegationNumber.name')"
                :placeholder="
                  $store.state.Auth.userProperties.system_settings.delegation_number_setting_type !== 1
                    ? $t('delegation.form.fields.delegationNumber.willBeAuto')
                    : null
                "
                v-on="on"
                v-model="delegation.custom_number"
                :readonly="$store.state.Delegation.readonly === true"
              />
            </template>
            {{ $t("delegation.form.fields.delegationNumber.leftEmptyToAutomatic") }}
          </v-tooltip>
        </v-col>
        <v-col md="6" sm="6" cols="12" v-if="$store.getters['userProperties'].system_settings.is_delegation_task_enabled === 2">
          <v-select
            :error-messages="delegationErrors.task_id"
            :items="delegation_tasks"
            :label="$t('delegation.form.fields.task.name')"
            v-model="delegation.task_id"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col md="6" sm="6" cols="12">
          <location-ajax-select-multiple
            :default-items="delegation.destinationObjects"
            :label="$t('delegation.form.fields.destination.name')"
            :placeholder="delegation.destinationText"
            :select-model.sync="delegation.destination"
            :select-validation="delegationErrors.destination"
            :multiple="true"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="12" v-if="companies.length === 1 && $store.getters['userProperties'].system_settings.is_delegation_task_enabled === 2">
          <v-text-field
            :error-messages="delegationErrors.task"
            :label="$t('delegation.form.fields.taskDescription.name')"
            v-model="delegation.task"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="12" md="6" sm="6" v-else-if="$store.getters['userProperties'].system_settings.is_delegation_task_enabled === 2">
          <v-text-field
            :error-messages="delegationErrors.task"
            :label="$t('delegation.form.fields.taskDescription.name')"
            v-model="delegation.task"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="6" v-if="isReInvoicingForExternalContractorModuleEnabled">
          <v-autocomplete
            :items="externalCustomers"
            v-model="delegation.external_customer_id"
            :error-messages="delegationErrors.external_customer_id"
            clearable
            :label="$t('delegation.form.fields.externalCustomer.name')"
            @change="changedExternalCustomer"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="6" v-if="isReInvoicingForExternalContractorModuleEnabled">
          <v-autocomplete
            :items="projectsForExternalCustomer"
            v-model="delegation.project_id"
            clearable
            :label="$t('delegation.form.fields.project.name')"
            :error-messages="delegationErrors.project_id"
            :readonly="$store.state.Delegation.readonly === true"
            persistent-hint
            :hint="
              typeof projects.find(v => v.value === delegation.project_id) === 'undefined'
                ? ''
                : $t('global.orderNumber') + ': ' + projects.find(v => v.value === delegation.project_id).order_number
            "
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col md="6" sm="6" cols="12">
          <date-time-picker-model-computed
            :time-required="true"
            :date-label="$t('delegation.form.fields.startDate.dateName')"
            :date-time-validation="delegationErrors.start_date"
            :dateTimeModel.sync="delegation.start_date"
            :time-label="$t('delegation.form.fields.startDate.timeName')"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col md="6" sm="6" cols="12">
          <date-time-picker-model-computed
            :time-required="true"
            :date-label="$t('delegation.form.fields.endDate.dateName')"
            :date-time-validation="delegationErrors.end_date"
            :dateTimeModel.sync="delegation.end_date"
            :time-label="$t('delegation.form.fields.endDate.timeName')"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col md="12" sm="12" cols="12">
          <collapse-simple-advance-panel
            :clearable="$store.state.Delegation.readonly !== true"
            :addable="$store.state.Delegation.readonly !== true"
            :model-advances.sync="delegation.advances"
            :validation-model="delegationErrors.advances"
            v-on:addAdvanceItem="addDelegationAdvance"
            v-on:removeAdvanceItem="removeAdvance"
            :max-date="delegation.end_date"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col v-if="$store.state.Auth.userProperties.system_settings.cost_centre_on_delegations !== 3" cols="6">
          <v-select
            :error-messages="delegationErrors.cost_centre_id"
            :items="availableCostCentres"
            :label="$t('delegation.form.fields.costCentre.name')"
            :clearable="$store.state.Delegation.readonly !== true"
            item-text="name"
            item-value="id"
            v-model="delegation.cost_centre_id"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="6">
          <date-picker
            :label="$t('delegation.form.fields.settledAt.name')"
            :model.sync="delegation.settlement_date"
            :readonly="$store.state.Delegation.readonly === true"
          />
        </v-col>
        <v-col cols="6" v-if="$store.getters['userProperties'].system_settings.is_module_optima_enabled">
          <optima-category-select :value.sync="delegation.optima_category_code" />
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import DateTimePickerModelComputed from "../../CustomInputComponents/DateTimePickerModelComputed.vue";
import DelegationModel from "../../../models/delegation";
import createErrorArray from "../../../repository/errorObjectArray";
import CollapseSimpleAdvancePanel from "../../Advance/CollapseSimpleAdvancePanel.vue";
import DelegationOrderSelect from "../../CustomInputComponents/DelegationOrderSelect";
import LocationAjaxSelectMultiple from "../../CustomInputComponents/LocationAjaxSelectMultiple";
import DatePicker from "../../CustomInputComponents/DatePicker";
import OptimaCategorySelect from "../../CustomInputComponents/OptimaCategorySelect";

const delegationModel = DelegationModel;

export default {
  name: "General",
  props: {
    // delegation: {
    //   type: Object,
    //   required: true
    // },
    delegationErrors: {
      type: Object,
      required: false,
      default: function() {
        return createErrorArray(this.delegation);
      }
    }
  },
  components: {
    OptimaCategorySelect,
    DatePicker,
    LocationAjaxSelectMultiple,
    DelegationOrderSelect,
    DateTimePickerModelComputed,
    CollapseSimpleAdvancePanel
  },
  data: () => ({
    downloaded_delegation_tasks: [],
    editedItem: delegationModel,
    projects: [],
    companies: [],
    externalCustomers: []
  }),
  computed: {
    delegation() {
      return this.$store.getters["Delegation/delegationEdited"];
    },
    projectsForExternalCustomer() {
      return this.projects.filter(v => v.external_customer_id === this.delegation.external_customer_id);
    },
    delegation_tasks() {
      let result = [];
      for (let delegationTask of this.downloaded_delegation_tasks) {
        result.push({
          value: delegationTask.id.toString(),
          text:
            this.$i18n.locale === "pl" || delegationTask.name_en == null || delegationTask.name_en.length < 1 ? delegationTask.name : delegationTask.name_en,
          disabled: delegationTask.disabled
        });
      }
      return result;
    },
    users() {
      return this.$store.state.Delegation.delegationAvailableUsers;
    },
    availableCostCentres() {
      let userProperties = this.$store.state.Delegation.delegationAvailableUsers.find(v => v.id.toString() === this.delegation.user_id.toString());
      if (userProperties != null) {
        return userProperties.availableCostCentres;
      }
      return [];
    },
    contextUser: {
      set(value) {
        if (this.delegation.user_id !== "") {
          this.$store.dispatch("snackbarPush", {
            text: this.$t("delegation.form.userChanged"),
            color: "info",
            position: "down"
          });
        }
        if (this.users.length > 0) {
          this.delegation.user_id = value.toString();
          let userProperties = this.$store.state.Delegation.delegationAvailableUsers.find(v => v.id.toString() === this.delegation.user_id);
          if (userProperties != null) {
            this.delegation.company_id = userProperties.company_id;
            if (userProperties.defaultCostCentre != null) {
              this.delegation.cost_centre_id = userProperties.defaultCostCentre.id;
            } else {
              this.delegation.cost_centre_id = null;
            }
          }
        }
      },
      get() {
        return parseInt(this.delegation.user_id);
      }
    },
    isReInvoicingForExternalContractorModuleEnabled() {
      return this.$store.getters["userProperties"].system_settings.external_customers_module === 2;
    }
  },
  created() {
    this.axios.get("delegation/get-delegations-select-data").then(response => {
      this.downloaded_delegation_tasks = response.data.tasks;
      this.externalCustomers = response.data.external_customers;
      this.projects = response.data.projects;
      this.companies = response.data.companies;
    });
  },
  methods: {
    changedExternalCustomer() {
      this.delegation.project_id = null;
    },
    addDelegationAdvance() {
      if (this.$store.state.Delegation.readonly !== true) {
        this.$store.dispatch("Delegation/addAdvance");
      }
    },
    removeAdvance(item) {
      this.$store.dispatch("Delegation/removeAdvance", item);
    }
  }
};
</script>

<style scoped></style>
